import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useRegularProjects } from '../hooks/useProjects';
import { useProjectRisk } from '../hooks/useInsights';
import { useUserStore } from '../store/userStore';
import { track } from '../lib/telemetry';
import { DashboardHeader } from '../components/DashboardHeader';
import { listGoals } from '../lib/api';
import { queryKeys } from '../lib/queryKeys';
import type { Project, RiskFacet, Goal } from '../types';
import { RiskBadge } from '../components/RiskBadge';
import { getRiskBand, DEFAULT_ALERT_THRESHOLD } from '../lib/riskBand';
import { confidenceFromScore } from '../lib/deliveryConfidence';

const FACET_NAMES: RiskFacet['name'][] = [
  'Communication',
  'Schedule',
  'Definition',
  'Scope',
  'Dependency',
  'Deadline',
];

// Deliberately kept graduated (not the shared 3-band model): this feeds the
// per-facet risk heatmap below, a dense grid where the whole point is
// spotting gradient patterns across six facets x many projects at a glance.
// Flattening it to High/Medium/Low would erase the pattern the heatmap
// exists to show. The per-project "overall" summary badges use the shared
// RiskBadge/getRiskBand instead — see RiskHeatmapRow and PortfolioHealthBar.
function scoreToBg(score: number): string {
  if (score <= 1) return 'bg-green-600';
  if (score <= 2) return 'bg-teal-600';
  if (score <= 3) return 'bg-amber-500';
  if (score <= 4) return 'bg-orange-600';
  return 'bg-red-600';
}

const KR_STATUS_COLORS: Record<string, string> = {
  not_started: 'bg-gray-200 dark:bg-white/10',
  in_progress: 'bg-blue-400',
  at_risk: 'bg-orange-400',
  achieved: 'bg-green-500',
};

function GoalProgressBar({ value, thin = false }: { value: number; thin?: boolean }) {
  const color = value >= 100 ? 'bg-green-500' : value >= 70 ? 'bg-blue-500' : value >= 40 ? 'bg-amber-400' : 'bg-gray-300 dark:bg-white/10';
  return (
    <div className={`w-full bg-gray-100 dark:bg-white/10 rounded-full overflow-hidden ${thin ? 'h-1' : 'h-2'}`}>
      <motion.div
        initial={{ width: 0 }}
        animate={{ width: `${value}%` }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className={`h-full rounded-full ${color}`}
      />
    </div>
  );
}

function GoalProgressSection({ goals, projects }: { goals: Goal[]; projects: Project[] }) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const active = goals.filter((g) => g.status === 'active');
  if (active.length === 0) return null;

  return (
    <div className="bg-white dark:bg-bg-surface border border-gray-100 dark:border-white/[0.06] rounded-xl p-5 mb-6">
      <h2 className="text-meta font-semibold text-gray-500 dark:text-gray-400 mb-6 uppercase tracking-wider">
        OKR Progress
      </h2>
      <div className="space-y-4">
        {active.map((goal) => {
          const avgProgress = goal.keyResults.length
            ? Math.round(goal.keyResults.reduce((s, kr) => s + kr.progress, 0) / goal.keyResults.length)
            : 0;
          const linkedProjects = projects.filter((p) =>
            (p.linkedKrIds ?? []).some((l) => l.goalId === goal.goalId)
          );
          const isOpen = expandedId === goal.goalId;

          return (
            <div key={goal.goalId} className="border border-gray-100 dark:border-white/[0.06] rounded-xl overflow-hidden">
              <button
                onClick={() => setExpandedId(isOpen ? null : goal.goalId)}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-white/[0.03] transition-colors text-left"
              >
                <ChevronDown className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5">
                    <p className="text-title text-gray-900 dark:text-white truncate">{goal.title}</p>
                    {goal.timeframe && (
                      <span className="text-micro px-1.5 py-0.5 rounded bg-gray-100 dark:bg-white/10 text-gray-500 dark:text-gray-400 flex-shrink-0">
                        {goal.timeframe}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <GoalProgressBar value={avgProgress} />
                    <span className="text-meta text-gray-500 dark:text-gray-400 flex-shrink-0 w-8 text-right">{avgProgress}%</span>
                  </div>
                </div>
                {linkedProjects.length > 0 && (
                  <span className="text-micro text-gray-400 flex-shrink-0">
                    {linkedProjects.length} project{linkedProjects.length !== 1 ? 's' : ''}
                  </span>
                )}
              </button>

              <AnimatePresence>
                {isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className="overflow-hidden"
                  >
                    <div className="px-4 pb-4 pt-1 bg-gray-50 dark:bg-white/[0.01] space-y-4">
                      {goal.description && (
                        <p className="text-meta text-gray-500 dark:text-gray-400 max-w-2xl">{goal.description}</p>
                      )}

                      {goal.keyResults.length > 0 && (
                        <div className="space-y-4">
                          {goal.keyResults.map((kr) => (
                            <div key={kr.krId} className="flex items-center gap-3">
                              <span className={`h-2 w-2 rounded-full flex-shrink-0 ${KR_STATUS_COLORS[kr.status] ?? 'bg-gray-200'}`} />
                              <div className="flex-1 min-w-0">
                                <p className="text-meta text-gray-700 dark:text-gray-300 truncate">{kr.title}</p>
                                {kr.metric && <p className="text-meta text-gray-400">{kr.metric}</p>}
                              </div>
                              <div className="w-24">
                                <GoalProgressBar value={kr.progress} thin />
                              </div>
                              <span className="text-meta text-gray-400 w-8 text-right flex-shrink-0">{kr.progress}%</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {linkedProjects.length > 0 && (
                        <div>
                          <p className="text-meta font-medium text-gray-500 dark:text-gray-400 mb-1.5">Linked projects</p>
                          <div className="flex flex-wrap gap-1.5">
                            {linkedProjects.map((p) => (
                              <span key={p.projectId}
                                className="text-meta px-2 py-0.5 rounded-full bg-white dark:bg-white/10 border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-300">
                                {p.name}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PortfolioHealthBar({ projects }: { projects: Project[] }) {
  const alertThreshold = useUserStore((s) => s.sessionConfig?.alertThreshold) ?? DEFAULT_ALERT_THRESHOLD;
  // projects is already Parking Lot-free — see useRegularProjects() in ExecutiveDashboard().
  const regular = projects;
  const scores = regular
    .map((p) => p.overallRiskScore)
    .filter((s): s is number => s !== null);
  const avg =
    scores.length > 0
      ? Math.round((scores.reduce((a, b) => a + b, 0) / scores.length) * 10) / 10
      : null;
  const critical = regular.filter(
    (p) => getRiskBand(p.overallRiskScore, alertThreshold) === 'high'
  ).length;
  const onTrack = regular.filter(
    (p) => getRiskBand(p.overallRiskScore, alertThreshold) === 'low'
  ).length;
  const complete = regular.filter((p) => p.status === 'complete').length;

  const stats = [
    { label: 'Avg Risk Score', value: avg !== null ? String(avg) : '—', sub: 'portfolio-wide' },
    { label: 'Critical', value: String(critical), sub: 'High band' },
    { label: 'On Track', value: String(onTrack), sub: 'Low band' },
    { label: 'Complete', value: String(complete), sub: 'projects' },
  ];

  return (
    <div className="bg-white dark:bg-bg-surface border border-gray-100 dark:border-white/[0.06] rounded-xl p-5 mb-6">
      <h2 className="text-meta font-semibold text-gray-500 dark:text-gray-400 mb-6 uppercase tracking-wider">
        Portfolio Health
      </h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {stats.map((s) => (
          <div key={s.label} className="text-center">
            <p className="text-4xl font-bold text-gray-900 dark:text-white tracking-tight">{s.value}</p>
            <p className="text-meta font-medium text-gray-500 dark:text-gray-400 mt-1">{s.label}</p>
            <p className="text-meta text-gray-400 dark:text-gray-500">{s.sub}</p>
          </div>
        ))}
      </div>
      {/* "Trend vs last week: —" removed: it was a hardcoded placeholder that
          promised a computation which never existed. Reinstate only when a
          real portfolio-level trend is computed. */}
    </div>
  );
}

function RiskHeatmapRow({ project }: { project: Project }) {
  const [expanded, setExpanded] = useState(false);
  // Always load — so cells show color without needing to expand
  const { data: riskCache, isLoading } = useProjectRisk(project.projectId);

  const facetScore = (name: RiskFacet['name']): number | null => {
    if (!riskCache) return null;
    return riskCache.facets?.find((f) => f.name === name)?.score ?? null;
  };

  return (
    <>
      <tr
        className="border-b border-gray-100 dark:border-white/[0.06] hover:bg-gray-50 dark:hover:bg-white/[0.02] cursor-pointer transition-colors"
        onClick={() => setExpanded((v) => !v)}
      >
        <td className="py-4 px-4">
          <div className="flex items-center gap-2">
            <ChevronDown
              className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`}
            />
            <span className="text-title text-gray-900 dark:text-white truncate max-w-[280px]">
              {project.name}
            </span>
          </div>
        </td>
        {FACET_NAMES.map((name) => {
          const s = facetScore(name);
          // When facet data isn't available but we have an overall score, show a dim fallback
          const fallback = !isLoading && s === null && project.overallRiskScore !== null;
          return (
            <td key={name} className="py-4 px-2 text-center">
              {isLoading ? (
                <span className="inline-block h-6 w-6 rounded animate-pulse bg-gray-100 dark:bg-white/10" />
              ) : s !== null ? (
                <span
                  className={`inline-flex items-center justify-center h-6 w-6 rounded text-micro font-bold text-white ${scoreToBg(s)}`}
                  title={`${name}: ${s}`}
                >
                  {s.toFixed(0)}
                </span>
              ) : fallback ? (
                <span
                  className={`inline-block h-6 w-6 rounded opacity-25 ${scoreToBg(project.overallRiskScore!)}`}
                  title="No facet data — showing overall score"
                />
              ) : (
                <span className="inline-block h-6 w-6 rounded bg-gray-100 dark:bg-white/10" />
              )}
            </td>
          );
        })}
        <td className="py-4 px-4 text-right">
          <RiskBadge score={project.overallRiskScore} size="sm" />
        </td>
      </tr>
      <AnimatePresence>
        {expanded && (
          <tr>
            <td
              colSpan={FACET_NAMES.length + 2}
              className="bg-gray-50 dark:bg-white/[0.02] px-4 py-4"
            >
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden"
              >
                {riskCache ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {(riskCache.facets ?? []).map((facet) => (
                      <div
                        key={facet.name}
                        className="bg-white dark:bg-bg-surface rounded-lg p-3 border border-gray-100 dark:border-white/[0.06]"
                      >
                        <div className="flex items-center gap-2 mb-1.5">
                          <span className={`inline-flex items-center justify-center h-5 w-5 rounded text-micro font-bold text-white flex-shrink-0 ${scoreToBg(facet.score)}`}>
                            {facet.score}
                          </span>
                          <span className="text-meta font-semibold text-gray-700 dark:text-gray-300">
                            {facet.name}
                          </span>
                        </div>
                        <p className="text-meta text-gray-500 dark:text-gray-400">
                          {facet.reasoning}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-micro text-gray-500">No risk data available.</p>
                )}
              </motion.div>
            </td>
          </tr>
        )}
      </AnimatePresence>
    </>
  );
}

function RiskHeatmap({ projects }: { projects: Project[] }) {
  // projects is already Parking Lot-free — see useRegularProjects() in ExecutiveDashboard().
  const regular = projects;

  return (
    <div className="bg-white dark:bg-bg-surface border border-gray-100 dark:border-white/[0.06] rounded-xl mb-6 overflow-hidden">
      <div className="px-5 pt-5 pb-3">
        <h2 className="text-meta font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
          Risk Heatmap
        </h2>
        <p className="text-meta text-gray-400 dark:text-gray-500 mt-0.5">
          Click a row to expand facet reasoning
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-100 dark:border-white/[0.06]">
              <th className="py-4 px-4 text-left text-meta font-medium text-gray-500 dark:text-gray-400 align-bottom">
                Project
              </th>
              {FACET_NAMES.map((name) => (
                <th
                  key={name}
                  className="px-2"
                  style={{ height: '72px', verticalAlign: 'bottom' }}
                >
                  <div className="flex justify-center items-end pb-2" style={{ height: '100%' }}>
                    <div
                      className="text-meta font-medium text-gray-500 dark:text-gray-400"
                      style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', whiteSpace: 'nowrap' }}
                    >
                      {name}
                    </div>
                  </div>
                </th>
              ))}
              <th className="py-4 px-4 text-right text-meta font-medium text-gray-500 dark:text-gray-400 align-bottom">
                Score
              </th>
            </tr>
          </thead>
          <tbody>
            {regular.map((project) => (
              <RiskHeatmapRow key={project.projectId} project={project} />
            ))}
            {regular.length === 0 && (
              <tr>
                <td
                  colSpan={FACET_NAMES.length + 2}
                  className="py-8 text-center text-meta text-gray-500"
                >
                  No projects found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DeliveryConfidence({ projects }: { projects: Project[] }) {
  // projects is already Parking Lot-free — see useRegularProjects() in ExecutiveDashboard().
  const regular = [...projects].sort(
    (a, b) => (b.overallRiskScore ?? 0) - (a.overallRiskScore ?? 0)
  );

  return (
    <div className="bg-white dark:bg-bg-surface border border-gray-100 dark:border-white/[0.06] rounded-xl p-5 mb-6">
      <h2 className="text-meta font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-6">
        Delivery Confidence
      </h2>
      <div className="space-y-4">
        {regular.map((project) => {
          const pct = confidenceFromScore(project.overallRiskScore);
          // Unscored (null) must read as unknown — an empty neutral bar —
          // never as 0% red, which would fabricate a confident "doomed".
          const barColor =
            pct === null ? '' :
            pct >= 80 ? 'bg-green-500' :
            pct >= 60 ? 'bg-amber-400' :
            pct >= 40 ? 'bg-orange-500' : 'bg-red-600';
          const textColor =
            pct === null ? 'text-gray-400 dark:text-gray-500' :
            pct >= 80 ? 'text-green-600 dark:text-green-400' :
            pct >= 60 ? 'text-amber-600 dark:text-amber-400' :
            pct >= 40 ? 'text-orange-500 dark:text-orange-400' : 'text-red-600 dark:text-red-400';
          return (
            <div key={project.projectId} className="space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-title text-gray-900 dark:text-white truncate flex-1 mr-3">
                  {project.name}
                </span>
                <span className={`text-body font-bold flex-shrink-0 ${textColor}`}>
                  {pct !== null ? `${pct}%` : '—'}
                </span>
              </div>
              <div className="h-1.5 bg-gray-100 dark:bg-white/10 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${pct ?? 0}%` }}
                  transition={{ duration: 0.5, ease: 'easeOut' }}
                  className={`h-full rounded-full ${barColor}`}
                />
              </div>
            </div>
          );
        })}
        {regular.length === 0 && (
          <p className="text-meta text-gray-500">No projects.</p>
        )}
      </div>
    </div>
  );
}

function ProjectSkeleton() {
  return (
    <div className="bg-white dark:bg-bg-surface border border-gray-100 dark:border-white/[0.06] rounded-xl p-5 space-y-4">
      <div className="skeleton h-4 w-2/3 rounded" />
      <div className="skeleton h-3 w-full rounded" />
      <div className="skeleton h-3 w-4/5 rounded" />
    </div>
  );
}

export function ExecutiveDashboard({ onBackToToday }: { onBackToToday?: () => void } = {}) {
  const { sessionConfig } = useUserStore();
  const { data: projects, isLoading } = useRegularProjects();
  const { data: goals = [] } = useQuery({ queryKey: queryKeys.goals, queryFn: listGoals });

  const effectiveRole: string =
    sessionConfig?.entityRole ??
    sessionConfig?.role ??
    sessionConfig?.functionalRoles?.[0] ??
    'viewer';
  const effectiveRoleRef = useRef(effectiveRole);
  effectiveRoleRef.current = effectiveRole;

  useEffect(() => {
    const start = Date.now();
    track('dashboard.executive.opened', 'page_view', { role: effectiveRoleRef.current });
    return () => {
      track('dashboard.executive.time_spent', 'page_view', {
        role: effectiveRoleRef.current,
        durationMs: Date.now() - start,
      });
    };
  }, []);

  return (
    <div className="px-6 py-6 sm:px-10 lg:px-16">
      <DashboardHeader title="Executive Overview" roleLabel="Executive" onBackToToday={onBackToToday} />

      {isLoading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <ProjectSkeleton key={i} />
          ))}
        </div>
      ) : (
        <>
          <GoalProgressSection goals={goals} projects={projects ?? []} />
          <PortfolioHealthBar projects={projects ?? []} />
          <RiskHeatmap projects={projects ?? []} />
          <DeliveryConfidence projects={projects ?? []} />
        </>
      )}
    </div>
  );
}
